import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '../services/authService';
import { updateMyProfile, changeMyPassword } from '../services/usuarioService';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, CardHeader, FormField, Input, Modal, PageHeader } from './ui';
import { ActiveTab } from '../types';

interface ConfiguracionProps {
  onNavigate?: (tab: ActiveTab) => void;
}

const mensajeDeError = (err: any, alternativa: string): string =>
  err?.response?.data?.message || err?.message || alternativa;

const Configuracion: React.FC<ConfiguracionProps> = ({ onNavigate }) => {
  const currentUser = getCurrentUser();

  const [perfil, setPerfil] = useState({
    nombre: currentUser?.nombre || '',
    apellido: currentUser?.apellido || '',
    email: currentUser?.email || '',
  });
  const [perfilGuardando, setPerfilGuardando] = useState(false);
  const [perfilMsg, setPerfilMsg] = useState<{ texto: string; tipo: 'success' | 'error' } | null>(null);

  // Mientras la contraseña siga siendo la temporal, cambiarla es lo único que
  // el usuario puede hacer. Se guarda en estado para que la pantalla reaccione
  // apenas la cambia, sin depender de una recarga.
  const [debeCambiarClave, setDebeCambiarClave] = useState((currentUser?.estado_pass ?? 1) === 0);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'info' | 'success' | 'error'>('info');
  const [modalMsg, setModalMsg] = useState('');
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined);
  const [modalConfirmText, setModalConfirmText] = useState<string | undefined>(undefined);
  const [modalConfirmHandler, setModalConfirmHandler] = useState<(() => void) | undefined>(undefined);

  const openModal = (opts: {
    message: string;
    type?: 'info' | 'success' | 'error';
    title?: string;
    confirmText?: string;
    onConfirm?: () => void;
  }) => {
    setModalTitle(opts.title);
    setModalMsg(opts.message);
    setModalType(opts.type || 'info');
    setModalConfirmText(opts.confirmText);
    setModalConfirmHandler(() => opts.onConfirm);
    setModalOpen(true);
  };

  useEffect(() => {
    if (debeCambiarClave && !sessionStorage.getItem('pwdNoticeShown')) {
      openModal({
        title: 'Atención',
        message: 'Debe cambiar su contraseña temporal antes de continuar.',
        type: 'info',
      });
      sessionStorage.setItem('pwdNoticeShown', '1');
    }
  }, [debeCambiarClave]);

  const irAlPanel = () => {
    setModalOpen(false);
    if (onNavigate) {
      onNavigate('dashboard');
      return;
    }
    window.location.href = `${process.env.PUBLIC_URL || ''}/`;
  };

  const handleSaveProfile = async () => {
    setPerfilMsg(null);
    setPerfilGuardando(true);
    try {
      const updated = await updateMyProfile(perfil);
      const uStr = localStorage.getItem('user');
      if (uStr) {
        const u = JSON.parse(uStr);
        localStorage.setItem(
          'user',
          JSON.stringify({ ...u, nombre: updated.nombre, apellido: updated.apellido, email: updated.email })
        );
      }
      setPerfilMsg({ texto: 'Los datos se actualizaron correctamente.', tipo: 'success' });
    } catch (err) {
      setPerfilMsg({ texto: mensajeDeError(err, 'No se pudieron actualizar los datos.'), tipo: 'error' });
    } finally {
      setPerfilGuardando(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      openModal({ title: 'Datos incompletos', message: 'Complete todos los campos de contraseña.', type: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      openModal({
        title: 'Las contraseñas no coinciden',
        message: 'La nueva contraseña y su confirmación deben ser iguales.',
        type: 'error',
      });
      return;
    }

    setPwdSaving(true);
    try {
      await changeMyPassword({ oldPassword, newPassword });

      const uStr = localStorage.getItem('user');
      if (uStr) {
        const u = JSON.parse(uStr);
        localStorage.setItem('user', JSON.stringify({ ...u, estado_pass: 1 }));
      }
      // La pantalla deja de exigir el cambio de inmediato: si el usuario cierra
      // el aviso en lugar de ir al panel, queda con la aplicación completa.
      setDebeCambiarClave(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      openModal({
        title: 'Contraseña actualizada',
        message: 'Su contraseña se actualizó correctamente. Ya puede utilizar el resto de la aplicación.',
        type: 'success',
        confirmText: 'Ir al panel',
        onConfirm: irAlPanel,
      });
    } catch (err) {
      openModal({
        title: 'No se pudo cambiar la contraseña',
        message: mensajeDeError(err, 'Verifique que la contraseña actual sea correcta.'),
        type: 'error',
      });
    } finally {
      setPwdSaving(false);
    }
  };

  const modalIcon =
    modalType === 'error' ? (
      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
    ) : modalType === 'success' ? (
      <CheckCircleIcon className="h-6 w-6 text-green-600" />
    ) : (
      <InformationCircleIcon className="h-6 w-6 text-blue-600" />
    );

  return (
    <div className="w-full py-5">
      {modalOpen && (
        <Modal
          title={modalTitle ?? (modalType === 'error' ? 'Error' : 'Aviso')}
          onClose={() => setModalOpen(false)}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              {modalConfirmHandler && modalConfirmText && (
                <Button variant={modalType === 'error' ? 'danger' : 'primary'} onClick={modalConfirmHandler}>
                  {modalConfirmText}
                </Button>
              )}
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Cerrar
              </Button>
            </div>
          }
        >
          <div className="flex items-start gap-3">
            {modalIcon}
            <p className="text-sm text-slate-700">{modalMsg}</p>
          </div>
        </Modal>
      )}

      <PageHeader
        title="Configuración de usuario"
        description="Datos personales y contraseña de su cuenta."
        action={
          !debeCambiarClave && (
            <Button variant="secondary" icon={<HomeIcon className="h-4 w-4" />} onClick={irAlPanel}>
              Ir al panel
            </Button>
          )
        }
      />

      <div className="mb-6 max-w-3xl">
        <Card>
          <CardHeader title="Mi perfil" />
          <div className="space-y-4">
            {perfilMsg && (
              <div
                className={`p-3 rounded-md text-sm ${
                  perfilMsg.tipo === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
                }`}
              >
                {perfilMsg.texto}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Nombre" htmlFor="perfil-nombre">
                <Input
                  id="perfil-nombre"
                  value={perfil.nombre}
                  onChange={e => setPerfil(p => ({ ...p, nombre: e.target.value }))}
                />
              </FormField>
              <FormField label="Apellido" htmlFor="perfil-apellido">
                <Input
                  id="perfil-apellido"
                  value={perfil.apellido}
                  onChange={e => setPerfil(p => ({ ...p, apellido: e.target.value }))}
                />
              </FormField>
              <FormField label="Email" htmlFor="perfil-email" className="md:col-span-2">
                <Input
                  id="perfil-email"
                  type="email"
                  value={perfil.email}
                  onChange={e => setPerfil(p => ({ ...p, email: e.target.value }))}
                />
              </FormField>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveProfile} loading={perfilGuardando}>
                Guardar cambios
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* El cambio de contraseña existe sólo para la clave temporal del primer
          ingreso. Después la administra el administrador, que la regenera y la
          envía por correo. */}
      <div className="mb-6 max-w-3xl">
        {debeCambiarClave ? (
          <Card>
            <CardHeader
              title="Debe cambiar su contraseña temporal"
              description="Por seguridad, defina una contraseña propia para poder utilizar el resto de la aplicación. Debe tener al menos 8 caracteres."
            />
            <div className="space-y-4">
              <FormField label="Contraseña temporal" htmlFor="old-password">
                <Input
                  id="old-password"
                  type="password"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                />
              </FormField>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Nueva contraseña" htmlFor="new-password">
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                  />
                </FormField>
                <FormField label="Confirmar nueva contraseña" htmlFor="confirm-password">
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                </FormField>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleChangePassword} loading={pwdSaving}>
                  Cambiar contraseña
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader title="Contraseña" />
            <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
              <p className="text-sm text-blue-800">
                Para actualizar/modificar la contraseña del usuario, contacte con el administrador.
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Configuracion;
